import { ListNsnCandidatesResponse, NsnCandidate, PartFinderBatchInputMethod } from '@/api/generatedApi';
import { useCreatePartFinderBatch } from '@/hooks/api/part-finder-batches/useCreatePartFinderBatch';
import { useCreatePartFinderReport } from '@/hooks/api/part-finder-reports/useCreatePartFinderReport';
import { useFindNsnCandidates } from '@/hooks/api/part-finder-reports/useFindNsnCandidates';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { useNavigate } from 'react-router-dom';
import { nsnCandidateLabel } from './NsnCandidateSummary';

const PART_NUMBER_ONLY_LABEL = 'Part number only (skip FLIS)';

export interface PartFinderSearchRequest {
  partNumber: string;
  description?: string;
  niin?: string;
  partNumberOnly?: boolean;
}

export interface PartFinderMultiNsnRequest {
  partNumber: string;
  description?: string;
  candidates: NsnCandidate[];
  partNumberOnly: boolean;
}

export const usePartFinderSearch = () => {
  const { mutateAsync: createReport, isPending: isCreating } = useCreatePartFinderReport();
  const { mutateAsync: createBatch, isPending: isCreatingBatch } = useCreatePartFinderBatch();
  const { findNsnCandidates, isPending: isFindingCandidates } = useFindNsnCandidates();
  const { showToast } = useToast();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const runSearch = async ({ partNumber, description, niin, partNumberOnly }: PartFinderSearchRequest) => {
    const response = await createReport({
      partNumber: partNumber.trim(),
      description: description?.trim() || undefined,
      niin,
      partNumberOnly,
    });
    showToast({
      message: 'Part finder report created successfully',
      type: 'success',
    });
    navigate(getPath(`part-finder-reports/${response.data.partFinderReport.id}`));
  };

  const runSearchForNsns = async ({
    partNumber,
    description,
    candidates,
    partNumberOnly,
  }: PartFinderMultiNsnRequest) => {
    const trimmedPartNumber = partNumber.trim();
    const trimmedDescription = description?.trim();
    const entries = [
      ...candidates.map(candidate => ({
        partNumber: trimmedPartNumber,
        description: [nsnCandidateLabel(candidate), trimmedDescription].filter(Boolean).join(' · '),
        niin: candidate.niin,
      })),
      ...(partNumberOnly
        ? [{ partNumber: trimmedPartNumber, description: trimmedDescription || PART_NUMBER_ONLY_LABEL, partNumberOnly }]
        : []),
    ];
    const response = await createBatch({
      entries,
      inputMethod: PartFinderBatchInputMethod.FlisSelect,
      name: `${trimmedPartNumber} (${entries.length} searches)`,
    });
    showToast({ message: `Started ${entries.length} reports for ${trimmedPartNumber}`, type: 'success' });
    navigate(getPath(`part-finder/batches/${response.data.partFinderBatch.id}`));
  };

  const searchOrListNsnCandidates = async (
    request: PartFinderSearchRequest
  ): Promise<ListNsnCandidatesResponse | undefined> => {
    const listing = await findNsnCandidates(request.partNumber.trim());
    if (listing.nsnCandidates.length > 1) {
      return listing;
    }
    await runSearch(request);
    return undefined;
  };

  return {
    runSearch,
    runSearchForNsns,
    searchOrListNsnCandidates,
    isPending: isCreating || isCreatingBatch || isFindingCandidates,
  };
};
